import type { PaymentLink } from '@nest-pay/shared'
import { useTranslation } from 'react-i18next'

interface PaymentDetailsProps {
  paymentLink: PaymentLink
}

export default function PaymentDetails({ paymentLink }: PaymentDetailsProps) {
  const { t, i18n } = useTranslation()
  const localeCode = i18n.language === 'pt' ? 'pt-BR' : i18n.language === 'en' ? 'en-US' : 'es-CL'
  const dateFormatter = new Intl.DateTimeFormat(localeCode, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const dateOnlyFormatter = new Intl.DateTimeFormat(localeCode, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

  const formatDate = (dateString: string) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      return dateOnlyFormatter.format(new Date(Date.UTC(year, month - 1, day)))
    }

    return dateFormatter.format(new Date(dateString))
  }

  return (
    <div className="space-y-2">
      <h3 className="text-[11px] font-medium text-neutral-700 uppercase tracking-wider font-sans">
        {t('payment.details')}
      </h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-neutral-600 font-light">{t('payment.reservation')}</dt>
          <dd className="font-light text-neutral-900">
            #{paymentLink.reservationId}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-neutral-600 font-light">{t('payment.guest')}</dt>
          <dd className="font-light text-neutral-900">
            {paymentLink.guestName}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-neutral-600 font-light">{t('payment.description')}</dt>
          <dd className="font-light text-neutral-900 text-right max-w-[200px]">
            {paymentLink.description}
          </dd>
        </div>
        {paymentLink.roomName && (
          <div className="flex justify-between">
            <dt className="text-neutral-600 font-light">{t('payment.room')}</dt>
            <dd className="font-light text-neutral-900 text-right max-w-[200px]">
              {paymentLink.roomName}
            </dd>
          </div>
        )}
        {(paymentLink.checkIn || paymentLink.checkOut) && (
          <div className="pt-3 mt-3 border-t border-neutral-100">
            <div className="flex justify-between items-center">
              <div className="text-center flex-1">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">{t('payment.checkIn')}</p>
                <p className="font-serif text-sm text-neutral-900">{paymentLink.checkIn ? formatDate(paymentLink.checkIn) : '—'}</p>
              </div>
              <div className="text-neutral-300 px-4">→</div>
              <div className="text-center flex-1">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">{t('payment.checkOut')}</p>
                <p className="font-serif text-sm text-neutral-900">{paymentLink.checkOut ? formatDate(paymentLink.checkOut) : '—'}</p>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-neutral-100">
          <div className="flex justify-between text-xs">
            <dt className="text-neutral-500 font-light">{t('payment.validUntil')}</dt>
            <dd className="text-neutral-500 font-light">
              {formatDate(paymentLink.expiresAt)}
            </dd>
          </div>
        </div>
      </dl>
    </div>
  )
}
